import { AxiosResponse } from 'axios';
import { IBrand } from './brand.types';
import { ICategory } from './categories.types';
import { IVariant } from './variant.types';
import { Response } from './general.types';

interface IMealsApi {
  getAllMeals: () => Promise<AxiosResponse>;
  getMeal: (slug: string) => Promise<AxiosResponse>;
  createMeal: (payload: ICreateMeal) => Promise<AxiosResponse>;
  updateMeal: (payload: ICreateMeal, id: number) => Promise<AxiosResponse>;

  generateMenu: (payload: { channel: string }) => Promise<AxiosResponse>;

  listAllFoodItems: (
    id?: string,
    params?: FoodItemParams
  ) => Response<FoodItem | FoodItem[]>;

  getAllFoodItems: (params?: FoodItemParams) => Response<FoodItem | FoodItem[]>;
  createFoodItem: (payload: CreateFoodItem<string>) => Response<FoodItem>;
  updateFoodItem: (
    id: string,
    payload: Partial<CreateFoodItem<string>>
  ) => Response<FoodItem>;

  getOptions: (params?: FoodItemParams) => Response<Option[]>;
  createOption: (payload: Option) => Response<{ data: Option }>;
  updateOption: (id: string, payload: Partial<Option>) => Response<Option>;

  getFoodGroups: (
    id?: string,
    params?: FoodItemParams
  ) => Response<FoodGroup[] | FoodGroup>;
  createFoodGroup: (
    payload: FoodGroup<Option, string, string>
  ) => Response<{ data: FoodGroup }>;
  updateFoodGroup: (
    id: string,
    payload: Partial<FoodGroup<string, string, string>>
  ) => Response<FoodGroup>;
  sendFoodItemsReport: (query: string) => Promise<AxiosResponse>;
  createDiscount: (payload: CreateFoodItemDiscount) => Promise<AxiosResponse>;
  updateItemDiscount: (
    id: string,
    payload: CreateFoodItemDiscount
  ) => Promise<AxiosResponse>;
  getFoodItemVariants: (id: string) => Response<any>;
  getMasterProductParent: (masterId: string) => Response<any>;
  getMasterProductForEdit: (
    masterId: string
  ) => Response<GetProductForEditResponse>;
  deleteFoodItems: ( payload: { ids: string[] }) => Response<any>
  setFoodItemOptionOnly: (id: string, payload: { optionOnly: boolean }) => Response<any>
  setBulkFoodItemOptionOnly: (payload: { ids: string[]; optionOnly: boolean }) => Response<any>
}

interface IChoice {
  title: string;
  variants: number[] | IVariant[];
}

interface ICreateMeal {
  name: string;
  status: MealStatus;
  description?: string;
  estimatedPreparationTime: number;
  basePrice: number;
  imageUrl?: any;
  brand: number | IBrand;
  kitchens?: number[];
  choices?: IChoice[];
  variants?: number[] | IVariant[];
  categories?: number[] | ICategory[];
  type: MealType;
}

interface IMeal extends ICreateMeal {
  id: number;
  slug: string;
  brand: IBrand;
  createdAt: string;
  updatedAt: string;
  bannerUrl?: string;
  brandId: number;
  variants?: IVariant[];
  categories: ICategory[];
}

type MealType = 'FIXED' | 'VARIABLE';

export type MealStatus = 'INACTIVE' | 'ACTIVE' | 'PREPARING' | 'OUT_OF_STOCK';

export type { IMealsApi, ICreateMeal, IMeal, IVariant, IChoice, MealType };

interface DeletePayload {
  subIds: string[];
  names: string[];
}

export interface DeleteParams {
  id: string;
  payload: DeletePayload;
}

export interface FoodItemParams {
  id?: string;
  latitude?: number;
  longitude?: number;
  brand?: number;
  userId?: number;
  category?: number;
  discounts?: boolean;
  count?: number;
  page?: number;
  search?: string;
  foodGroupOptionName?: string;
  foodGroupName?: string;
  displayCurrency?: string;
  groupByMaster?: boolean;
}

export interface FoodItem {
  id: string;
  createdAt: string | Date;
  updatedAt: string | Date;
  name: string;
  code: string;
  imageUrl?: string | null;
  description?: string | null;
  price: number;
  comboItem: boolean;
  status: FoodItemStatus;
  brandId: number | null;
  services: Services[];
  brand?: IBrand;
  images?: string[];
  categories?: ICategory[];
  foodGroup?: FoodGroup[] | string[];
  customisations?: FoodGroup[];
  discounts?: Discount | null;
  production?: any;
  aggregatedStock?: number;
  variantCount?: number;
  masterProductId?: string | null;
  parentItemId?: string | null;
}

export interface Production {
  id: string;
  createdAt: Date;
  updatedAt: Date;
  foodItemId: string;
  portions: number;
  yield: number;
  unit: ProductionUnit;
  status: ProductionStatus;
  preparationTime: number;
  closing: Date | null;
  foodItem: FoodItem;
}

export enum ProductionUnit {
  GRAM = 'GRAM',
  KILOGRAM = 'KILOGRAM',
}

export enum ProductionStatus {
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
}

export interface CreateFoodItem<T = CustomFoodGroup> {
  name: string;
  imageUrl?: string | null;
  description?: string | null;
  price: number;
  comboItem: boolean;
  status: FoodItemStatus;
  services: string[];
  brand?: number;
  categories?: number[];
  foodGroups: T[];
  customisations?: T[];
  thumbnailUrl?: string | null;
  thumbnailImages?: string[];
}

export interface CustomFoodGroup {
  id: string;
  position: number | null;
  name: string;
  identifier?: string | null;
}

export type FoodItemStatus = 'OPEN' | 'CLOSED' | 'OUT_OF_STOCK' | 'DELETED';

export type Services =
  | 'DELIVERY'
  | 'PICKUP'
  | 'SCHEDULED'
  | 'GROUPS'
  | 'EVENTS';

export interface Category {
  id: string;
  categoryId: number;
  brandId: number;
  position: number;
  estimatedProcessingTime: number;
  category: {
    id: number;
    slug: string;
    name: string;
    status: MealStatus;
    foodItems: FoodItem[];
  };
}
export interface FoodGroup<T = Option, F = FoodItem, C = Option> {
  id?: string;
  createdAt?: string;
  updatedAt?: string;
  name: string;
  position: number | null;
  minimumSelection?: number | null;
  maximumSelection?: number | null;
  options?: T[];
  status?: FoodItemStatus;
  foodItem?: F[];
  customisations?: C[];
  identifier?: null | string;
  colorIdentifier?: null | string;
}

export interface Option {
  id?: string;
  createdAt?: string;
  updatedAt?: string;
  name: string | null;
  price: number | null;
  minimumSelection?: number | null;
  maximumSelection?: number | null;
  sameAsFoodItemId?: string | null;
  sameAsFoodItem?: FoodItem | null;
  foodGroups?: string[];
  foodGroup?: FoodGroup[];
  foodItem?: string;
  active?: boolean;
  position?: number | null;
  status?: FoodItemStatus;
  image?: string | null;
}

export interface Discount {
  id: string;
  createdAt: Date;
  updatedAt: Date;
  enabled: boolean;
  brandId: number | null;
  kitchenId: number | null;
  mealId: number | null;
  percent: number;
  flat: number | null;
  rounding: number;
  foodItemId: string | null;
  brand?: {
    id: number;
    name: string;
    slug: string;
  };
}

export interface CreateFoodItemDiscount {
  foodItemId: string;
  percent?: number;
  flat?: number;
  rounding?: number;
  enabled: boolean;
  brandId?: number | null;
}

// Product For Edit Types
export interface ProductAttribute {
  name: string; // e.g., "Size", "Color"
  value: string; // e.g., "Large", "Red"
  isStockable: boolean;
}

export interface ProductVariantForEdit {
  id?: string;
  name?: string;
  attributes: ProductAttribute[]; // Array of variant attributes
  stock: number;
  quantity?: number;
  threshold: number;
  priceOverride?: number;
  price?: number;
  sku?: string;
  images?: string[];
}

export interface GetProductForEditResponse {
  id: string;
  masterProductId?: string | null;
  parentItemId?: string | null;
  productName: string;
  name?: string;
  description: string;
  productDetail?: string;
  media: {
    images: string[];
  };
  images?: string[];
  productOrganization: {
    collection?: string;
    categories: number[];
    subcategoryIds: number[];
    departmentIds: string[];
    productGroup?: string;
    productBrand?: string;
    tags: string[];
    status: string;
  };
  department?: {
    id: string;
    name: string;
  };
  category?: {
    id: number;
    name: string;
  };
  categories?: Array<{
    id: number;
    name: string;
  }>;
  SubCategories?: Array<{
    id: number;
    name: string;
  }>;
  foodGroup?: Array<{
    id: string;
    name: string;
  }>;
  tags?: string[];
  status?: string;
  pricing: {
    basePrice: number;
    currency: string;
    discountName?: string;
    discountType?: 'PERCENTAGE' | 'FIXED';
    discountValue?: number;
  };
  discount?: {
    name?: string;
    type?: 'PERCENTAGE' | 'FIXED';
    amount?: number;
  };
  brandId?: number;
  hasVariants: boolean;
  stockableAttributes?: string[];
  variants?: ProductVariantForEdit[];
  stockableChildren?: ProductVariantForEdit[];
}

export interface FoodItemInSubcategory {
  id: string;
  name: string;
  slug: string;
  basePrice?: number;
  price?: number;
  imageUrl: string | null;
  status: 'OPEN' | 'OUT_OF_STOCK';
  details?: {
    calories?: number;
    servings?: number;
  };
}

export interface FoodItemInSubcategoryResponse {
  appliedFilters: string[];
  products: FoodItemInSubcategory[];
}
